// Global/Multi-use
import { MutationKey, QueryKey } from '@tanstack/query-core'
import { GetNotificationsQueryParams } from 'src/app/Notifications/redux/notifications.actions'
import { ExpenseReportsFiltersBody } from 'src/components/ExpenseReportsBox/ExpenseReportsBox'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { VirtualCardsFiltersBody } from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { UserFeature } from 'src/models/user.models'
import { GetVirtualCardRevisionsQueryParams } from 'src/models/virtualCardRevision.models'
import {
  ExpenseCategoryFiltersRequestParams,
  ExpenseCategoryLabelFiltersBody,
} from 'src/queries/organizations/expenseCategories.queries'
import { GetOrgMembersRequest } from 'src/queries/organizations/members.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { GetCreditCardsRequest } from 'src/requests/getCreditCards.request'
import { GetUsersSearchRequest } from 'src/requests/getUsers.request'
import { GetVirtualCardsRequest } from 'src/requests/getVirtualCards.request'

export interface QueryCacheOptions {
  refetchOnMount: boolean,
  refetchOnWindowFocus: boolean,
  refetchOnReconnect: boolean,
  staleTime: number,
  gcTime: number,
}

export const defaultQueryCacheOptions: QueryCacheOptions = {
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 15 * 60 * 1000,
  gcTime: 15 * 60 * 1000,
}

export const queryCacheOptions = (
  overrides: Partial<QueryCacheOptions> = {},
): QueryCacheOptions => ({
  ...defaultQueryCacheOptions,
  ...overrides,
})

export const PERMISSIONS_QK = 'permissions'
export const MCC_GROUPS_QK = 'mccgroups'

type TransactionsListQueryKey = readonly [
  string,
  TransactionsFiltersBody | undefined,
]
// Transactions
export const TRANSACTIONS_QK = 'transactions'
export const TRANSACTIONS_COUNT_QK = 'count'
export const TRANSACTIONS_EXPENSE_DATA_QK = 'expensedata'
export const TRANSACTIONS_EXPENSE_CATEGORY_QK = 'categories'
export const TRANSACTIONS_EXPENSE_CATEGORY_LABELS_QK = 'labels'
export const ACCOUNTING_STATISTICS_QK = 'accountingstatistics'
export const transactionsQueryKeys = {
  all: [TRANSACTIONS_QK] as const,
  // WARNING: transaction.queries - useQueryFetchedTransactions relies on this order of the query keys
  list: (
    filters: TransactionsFiltersBody | undefined,
  ): TransactionsListQueryKey =>
    [...transactionsQueryKeys.all, filters] as const satisfies QueryKey,
  detail: (id: string | undefined): QueryKey =>
    [...transactionsQueryKeys.all, id] as const,
  count: (filters: TransactionsFiltersBody | undefined): QueryKey =>
    [...transactionsQueryKeys.all, TRANSACTIONS_COUNT_QK, filters] as const,
  expenseCategoryLabels: (
    transactionId: string | undefined,
    expenseCategoryId: string | undefined,
    filters: ExpenseCategoryLabelFiltersBody,
  ): QueryKey =>
    [
      ...transactionsQueryKeys.detail(transactionId),
      TRANSACTIONS_EXPENSE_DATA_QK,
      TRANSACTIONS_EXPENSE_CATEGORY_QK,
      expenseCategoryId,
      TRANSACTIONS_EXPENSE_CATEGORY_LABELS_QK,
      filters,
    ] as const,
  postSingleExpenseCategory: (): QueryKey =>
    [
      ...transactionsQueryKeys.all,
      TRANSACTIONS_EXPENSE_DATA_QK,
      TRANSACTIONS_EXPENSE_CATEGORY_QK,
    ] as const,
  accountingStatistics: (
    filters: TransactionsFiltersBody | undefined,
  ): QueryKey =>
    [...transactionsQueryKeys.list(filters), ACCOUNTING_STATISTICS_QK] as const,
}

// OOPE Expense Reports / Reimbursements
export const OOPE_QK = 'oope'
export const OOPE_APPROVER_QK = 'approver'
export const OOPE_SUBMITTER_QK = 'submitter'
export const OOPE_DIRECT_REPORTS_QK = 'oopedirectreports'
export const OOPE_COUNT_QK = 'count'
export const OOPE_REPORT_QK = 'report'
export const oopeQueryKeys = {
  all: [OOPE_QK] as const,
  // commented out because they arent implemented with react-query yet
  // list: (filters: ExpenseReportsFiltersBody | undefined): QueryKey => [...oopeQueryKeys.all, filters] as const satisfies QueryKey,
  approverList: (filters: ExpenseReportsFiltersBody | undefined): QueryKey =>
    [
      ...oopeQueryKeys.all,
      OOPE_APPROVER_QK,
      filters,
    ] as const satisfies QueryKey,
  submitterList: (filters: ExpenseReportsFiltersBody | undefined): QueryKey =>
    [
      ...oopeQueryKeys.all,
      OOPE_SUBMITTER_QK,
      filters,
    ] as const satisfies QueryKey,
  directReportsList: (
    filters: ExpenseReportsFiltersBody | undefined,
  ): QueryKey =>
    [
      ...oopeQueryKeys.all,
      OOPE_DIRECT_REPORTS_QK,
      filters,
    ] as const satisfies QueryKey,
  // detail: (id: string | undefined): QueryKey => [...oopeQueryKeys.all, id] as const,
  directReports: (): QueryKey =>
    [...oopeQueryKeys.all, OOPE_DIRECT_REPORTS_QK] as const,
  count: (): QueryKey =>
    [...oopeQueryKeys.directReports(), OOPE_COUNT_QK] as const,
  reportDetails: (reportId: string | undefined): QueryKey =>
    [...oopeQueryKeys.all, OOPE_REPORT_QK, reportId] as const,
}

// Organizations
export const ORG_QK = 'organizations'
export const EXPENSE_CATEGORIES_QK = 'expensecategories'
export const EXPENSE_CATEGORIES_LABELS_QK = 'labels'
export const EXPENSE_CATEGORIES_LABELS_BULK_TEMPLATE_QK = 'labelsbulktemplate'
export const EXPENSE_DATA_QK = 'expensedata'
export const CATEGORY_QK = 'category'
export const CATEGORIES_QK = 'categories'
export const ORG_FEATURE_FLAGS_QK = 'featureflags'
export const ORG_RECEIPT_RULES = 'receiptrules'
export const ORG_CARD_APPROVAL_RULES = 'approvalrules'
export const PHYSICAL_CLEARINGS = 'physicalClearings'
export const ORG_ACKNOWLEDGEMENTS = 'acknowledged'
export const DEPARTMENTS = 'departments'
export const MEMBERS = 'members'
export const PEOPLE_MANAGERS = 'peopleManagers'
export const DIRECT_REPORTS = 'directReports'
export const SAAS_TIER_QK = 'saas'
export const SUBSCRIPTION_URLS_QK = 'subscriptionurls'
export const ORG_USAGES_QK = 'orgusages'
export const ORG_SUBSCRIPTION_QK = 'orgsubscription'
export const REACTIVATE_SUB_QK = 'reactivatesubscription'
export const orgQueryKeys = {
  all: [ORG_QK] as const,
  detail: (orgId: string | undefined): QueryKey =>
    [...orgQueryKeys.all, orgId] as const,
  members: (
    orgId: string | undefined,
    queryParams: GetOrgMembersRequest,
  ): QueryKey => [...orgQueryKeys.detail(orgId), MEMBERS, queryParams] as const,
  expenseData: (
    filters: ExpenseCategoryFiltersRequestParams | undefined,
    additionalKey?: string,
  ): QueryKey => [
    EXPENSE_DATA_QK,
    CATEGORIES_QK,
    filters,
    ...(additionalKey ? [additionalKey] : []),
  ] as const,
  expenseDataDetail: (id: string | undefined): QueryKey =>
    [EXPENSE_DATA_QK, CATEGORIES_QK, id] as const,
  expenseDataLabels: (
    expenseCategoryId: string | undefined,
    filters: ExpenseCategoryLabelFiltersBody,
    additionalKey?: string,
  ): QueryKey =>
    [
      ...orgQueryKeys.expenseDataDetail(expenseCategoryId),
      EXPENSE_CATEGORIES_LABELS_QK,
      filters,
      ...(additionalKey ? [additionalKey] : []),
    ] as const,
  patchExpenseDataLabel: (): QueryKey =>
    [
      EXPENSE_DATA_QK,
      CATEGORIES_QK,
      EXPENSE_CATEGORIES_LABELS_QK,
      'patch',
    ] as const,
  expenseDataLabelDetail: (
    expenseCategoryId: string | undefined,
    labelId: string | undefined,
  ): QueryKey =>
    [
      ...orgQueryKeys.expenseDataDetail(expenseCategoryId),
      'labels',
      labelId,
    ] as const,
  departmentDetails: (
    orgId: string | undefined,
    departmentId: string | undefined,
  ): QueryKey =>
    [...orgQueryKeys.detail(orgId), DEPARTMENTS, departmentId] as const,
  departmentMembers: (
    orgId: string | undefined,
    departmentId: string | undefined,
  ): QueryKey =>
    [
      ...orgQueryKeys.detail(orgId),
      DEPARTMENTS,
      departmentId,
      MEMBERS,
    ] as const,
  departments: (
    orgId: string | undefined,
    includeArchived: boolean | undefined,
  ): QueryKey =>
    [...orgQueryKeys.detail(orgId), DEPARTMENTS, includeArchived] as const,
  featureFlags: (orgId: string | undefined): QueryKey =>
    [...orgQueryKeys.detail(orgId), ORG_FEATURE_FLAGS_QK] as const,
  receiptRules: (orgId: string | undefined): QueryKey =>
    [...orgQueryKeys.detail(orgId), ORG_RECEIPT_RULES] as const,
  approvalRules: (orgId: string | undefined): QueryKey =>
    [...orgQueryKeys.detail(orgId), ORG_CARD_APPROVAL_RULES] as const,
  physicalClearings: (orgId: string | undefined): QueryKey =>
    [...orgQueryKeys.detail(orgId), PHYSICAL_CLEARINGS] as const,
  acknowledged: (): QueryKey => [ORG_ACKNOWLEDGEMENTS] as const,
  createDepartment: (orgId: string | undefined): QueryKey =>
    [...orgQueryKeys.detail(orgId), DEPARTMENTS] as const,
  peopleManagers: (
    orgId: string | undefined,
    userId: string | undefined,
  ): QueryKey =>
    [...orgQueryKeys.detail(orgId), PEOPLE_MANAGERS, userId] as const,
  peopleManagerCount: (orgId: string | undefined): QueryKey =>
    [...orgQueryKeys.detail(orgId), PEOPLE_MANAGERS, 'count'] as const,
  peopleManagerDirectReports: (
    orgId: string | undefined,
    userId: string | undefined,
  ): QueryKey =>
    [...orgQueryKeys.detail(orgId), DIRECT_REPORTS, userId] as const,
  saasTier: (orgId: string | undefined): QueryKey =>
    [...orgQueryKeys.detail(orgId), SAAS_TIER_QK] as const,
  subscriptionUrls: (orgId: string | undefined): QueryKey =>
    [...orgQueryKeys.detail(orgId), SUBSCRIPTION_URLS_QK] as const,
  organizationUsages: (orgId: string | undefined): QueryKey =>
    [...orgQueryKeys.detail(orgId), ORG_USAGES_QK] as const,
  reactivateSubscription: (orgId: string | undefined): QueryKey =>
    [...orgQueryKeys.detail(orgId), REACTIVATE_SUB_QK] as const,
  organizationSubscription: (orgId: string | undefined): QueryKey =>
    [...orgQueryKeys.detail(orgId), ORG_SUBSCRIPTION_QK] as const,
}

// Statistics
export const STATISTICS_QK = 'statistics'
export const statisticsQueryKeys = {
  all: [STATISTICS_QK] as const,
  list: (statistics: StatisticsKeys[]) =>
    [...statisticsQueryKeys.all, ...[...statistics].sort()] as const,
}

// Users
export const USERS_QK = 'users'
export const USERS_ME_QK = 'me'
export const userQueryKeys = {
  all: [USERS_QK] as const,
  search: (filters: GetUsersSearchRequest | undefined, additionalKey?: string) =>
    [
      ...userQueryKeys.all,
      filters,
      ...(additionalKey ? [additionalKey] : []),
    ] as const,
  me: () => [...userQueryKeys.all, USERS_ME_QK] as const,
  detail: (id: string | undefined): QueryKey =>
    [...userQueryKeys.all, id] as const,
}

// Virtual Cards
export const VIRTUAL_CARDS_QK = 'virtualcards'
export const REVISIONS_QK = 'revisions'
export const virtualCardsQueryKeys = {
  all: [VIRTUAL_CARDS_QK] as const,
  list: (
    filtersBody: VirtualCardsFiltersBody | undefined,
    pagination?: { page?: number; perPage?: number },
  ): QueryKey =>
    [...virtualCardsQueryKeys.all, filtersBody, pagination] as const,
  detail: (id: string | undefined): QueryKey =>
    [...virtualCardsQueryKeys.all, id] as const,
  permissions: (id: string | undefined): QueryKey =>
    [...virtualCardsQueryKeys.detail(id), PERMISSIONS_QK] as const,
  revisions: (
    id: string | undefined,
    queryParams: GetVirtualCardRevisionsQueryParams | undefined,
  ) =>
    [...virtualCardsQueryKeys.detail(id), REVISIONS_QK, queryParams] as const,
}

export const virtualCardsPaginatedQueryKeys = {
  all: [VIRTUAL_CARDS_QK] as const,
  list: (filtersBody: GetVirtualCardsRequest | undefined): QueryKey =>
    [
      ...virtualCardsQueryKeys.all,
      {
        ...filtersBody,
        page: filtersBody?.page,
        count: filtersBody?.count,
      },
    ] as const,
  detail: (id: string | undefined): QueryKey =>
    [...virtualCardsQueryKeys.all, id] as const,
  permissions: (id: string | undefined): QueryKey =>
    [...virtualCardsQueryKeys.detail(id), PERMISSIONS_QK] as const,
  revisions: (
    id: string | undefined,
    queryParams: GetVirtualCardRevisionsQueryParams | undefined,
  ) =>
    [...virtualCardsQueryKeys.detail(id), REVISIONS_QK, queryParams] as const,
}

// Credit Cards
export const CREDIT_CARDS_QK = 'creditcards'
export const creditCardsQueryKeys = {
  all: [CREDIT_CARDS_QK] as const,
  list: (
    filters: GetCreditCardsRequest | undefined,
    additionalKey?: string,
  ): QueryKey =>
    [
      ...(additionalKey ? [additionalKey] : []),
      ...creditCardsQueryKeys.all,
      filters,
    ] as const,
  detail: (id: string | undefined): QueryKey =>
    [...creditCardsQueryKeys.all, id] as const,
  permissions: (id: string | undefined): QueryKey =>
    [...creditCardsQueryKeys.detail(id), PERMISSIONS_QK] as const,
}

// Issuers
export const ISSUERS_QK = 'issuers'
export const issuerQueryKeys = {
  all: [ISSUERS_QK] as const,
  detail: (id: string | undefined): QueryKey =>
    [...issuerQueryKeys.all, id] as const,
}

// Org Integrations
export const ORG_INTEGRATIONS_QK = 'orgintegrations'
export const orgIntegrationsQueryKeys = {
  all: [ORG_INTEGRATIONS_QK] as const,
  detail: (id: string | undefined): QueryKey =>
    [...orgIntegrationsQueryKeys.all, id] as const,
}

// Org Expense Reports
export const ORG_EXPENSE_REPORTS_QK = 'orgexpensereports'
export const ORG_EXPENSE_REPORTS_CYCLES_LIST_QK = 'reports'
export const ORG_EXPENSE_REPORTS_CYCLES_QK = 'cycles'
export const ORG_EXPENSE_REPORTS_METRICS_QK = 'metrics'
export const orgExpenseReportsQueryKeys = {
  cycles: [ORG_EXPENSE_REPORTS_QK, ORG_EXPENSE_REPORTS_CYCLES_QK] as const,
  cycleDetail: (cycleId: string | undefined): QueryKey =>
    [...orgExpenseReportsQueryKeys.cycles, cycleId] as const,
  historicalReports: (cycleId: string | undefined): QueryKey =>
    [
      ...orgExpenseReportsQueryKeys.cycles,
      cycleId,
      ORG_EXPENSE_REPORTS_CYCLES_LIST_QK,
    ] as const,
  detail: (reportId: string | undefined): QueryKey =>
    [ORG_EXPENSE_REPORTS_QK, reportId] as const,
  metrics: (reportId: string | undefined): QueryKey =>
    [
      ...orgExpenseReportsQueryKeys.detail(reportId),
      ORG_EXPENSE_REPORTS_METRICS_QK,
    ] as const,
}

// Receipt Reminders
export const RECEIPT_REMINDER_STATUS = 'receiptreminderstatus'
export const receiptReminderQueryKeys = {
  orgReceiptReminders: (orgId: string | undefined): QueryKey =>
    [ORG_QK, orgId, RECEIPT_REMINDER_STATUS] as const,
  orgExpenseReportReceiptReminders: (reportId: string | undefined): QueryKey =>
    [ORG_EXPENSE_REPORTS_QK, reportId, RECEIPT_REMINDER_STATUS] as const,
}

// API Users
export const API_USERS_QK = 'users'
export const API_KEYS_QK = 'apikeys'
export const apiUsersQueryKeys = {
  all: [API_KEYS_QK] as const,
  me: (): QueryKey => [...apiUsersQueryKeys.all, API_USERS_QK] as const,
  apiKeys: (): QueryKey => [...apiUsersQueryKeys.all] as const,
  apiKeyCreated: (id: string): QueryKey =>
    [...apiUsersQueryKeys.all, id] as const,
}

// Automation Rules
export const AUTOMATION_RULES_DATA_QK = 'rules'
export const AUTOMATION_RULES_DETAILS_QK = 'details'
export const automationRulesQueryKeys = {
  all: (): QueryKey => [AUTOMATION_RULES_DATA_QK] as const,
  detail: (ruleId: string | undefined): QueryKey =>
    [
      ...automationRulesQueryKeys.all(),
      ruleId,
      AUTOMATION_RULES_DETAILS_QK,
    ] as const,
  patch: (): MutationKey =>
    [...automationRulesQueryKeys.all(), 'patch'] as const,
}

// Authentication
export const FEATURES_QK = 'features'
export const authQueryKeys = {
  features: (featureKeys: UserFeature[]): QueryKey =>
    [FEATURES_QK, featureKeys] as const,
}

// USER TASKS
export const USER_TASK_STATS_QK = 'usertaskstats'
export const userTaskStatsQueryKeys = {
  userTaskStats: (): QueryKey => [USER_TASK_STATS_QK] as const,
}

// USER REQUIRED APPROVALS
export const USER_NEEDS_APPROVALS_STATS_QK = 'userneedsapprovalstats'
export const userNeedsApprovalsStatsQueryKeys = {
  userNeedsApprovalStats: (): QueryKey =>
    [USER_NEEDS_APPROVALS_STATS_QK] as const,
}

// Agent
const AGENT_QK = 'agent'
const AGENT_PROMPT_QK = 'prompt'
const AGENT_INSIGHTS_QK = 'insights'
export const agentQueryKeys = {
  all: () => [AGENT_QK] as const,
  prompt: () => [...agentQueryKeys.all(), AGENT_PROMPT_QK] as const,
  insights: () => [...agentQueryKeys.all(), AGENT_INSIGHTS_QK] as const,
}

export interface SimpleResponse {
  msg: string
}

// Activity Queries
export const NOTIFICATIONS_QK = 'notifications'
export const NOTIFICATIONS_LIST_QK = 'list'
export const NOTIFICATIONS_COUNT_QK = 'count'
export const notificationsQueryKeys = {
  all: () => [NOTIFICATIONS_QK] as const,
  list: () =>
    [
      ...notificationsQueryKeys.all(),
      NOTIFICATIONS_LIST_QK,
    ] as const satisfies QueryKey,
  listWithFilters: (filters: GetNotificationsQueryParams | undefined) =>
    [
      ...notificationsQueryKeys.all(),
      NOTIFICATIONS_LIST_QK,
      filters,
    ] as const satisfies QueryKey,
  detail: (id: string | undefined): QueryKey =>
    [...notificationsQueryKeys.all(), id] as const,
  count: (): QueryKey =>
    [...notificationsQueryKeys.all(), NOTIFICATIONS_COUNT_QK] as const,
}
